import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { church, fullAddress, nav } from "@/config/church";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setBusy(true);
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Subscribed!");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-npbc-black text-white/85">
      <div className="absolute inset-x-0 top-0 h-px divider-gold" />

      {/* Newsletter band */}
      <div className="container-npbc pt-20 pb-14">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] items-start">
          <div>
            <div className="flex items-center gap-4">
              <img src={church.logo} alt="NPBC logo" className="h-16 w-16 object-contain" />
              <div>
                <div className="heading-serif text-2xl text-white">New Philadelphia</div>
                <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-npbc-gold">
                  Missionary Baptist
                </div>
              </div>
            </div>
            <p className="mt-6 max-w-xl text-white/70 leading-relaxed">
              {church.slogan}. A Christ-centered community in the heart of Memphis
              where every guest is welcomed as family and every believer is challenged to grow.
            </p>
            <form onSubmit={subscribe} className="mt-8 flex max-w-md items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] p-1.5 backdrop-blur">
              <Mail className="ml-3 h-4 w-4 text-white/60" />
              <input
                data-testid="footer-newsletter-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email for our newsletter"
                className="flex-1 bg-transparent px-2 py-2.5 text-sm outline-none placeholder:text-white/45"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="footer-newsletter-submit"
                className="inline-flex items-center gap-2 rounded-full bg-npbc-red px-4 py-2.5 text-sm font-semibold hover:bg-[#a80d26] transition disabled:opacity-60"
              >
                {busy ? "Sending..." : "Subscribe"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:gap-12">
            <div>
              <div className="text-xs font-semibold tracking-[0.24em] uppercase text-npbc-gold">Explore</div>
              <ul className="mt-5 space-y-3 text-sm">
                {nav.map((n) => (
                  <li key={n.to}>
                    <Link
                      to={n.to}
                      className="text-white/75 hover:text-white transition"
                      data-testid={`footer-link-${n.label.toLowerCase()}`}
                    >
                      {n.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-xs font-semibold tracking-[0.24em] uppercase text-npbc-gold">Contact</div>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex gap-3 text-white/80">
                  <MapPin className="h-4 w-4 mt-0.5 text-npbc-red" />
                  <span>{fullAddress}</span>
                </li>
                <li className="flex gap-3 text-white/80">
                  <Phone className="h-4 w-4 mt-0.5 text-npbc-red" />
                  <a href={`tel:${church.phoneRaw}`} className="hover:text-white">{church.phone}</a>
                </li>
                <li className="flex gap-3 text-white/80">
                  <Mail className="h-4 w-4 mt-0.5 text-npbc-red" />
                  <a href={`mailto:${church.email}`} className="hover:text-white">{church.email}</a>
                </li>
              </ul>
              <div className="mt-6 flex items-center gap-3">
                {[
                  { href: church.social.facebook, Icon: Facebook, label: "Facebook" },
                  { href: church.social.instagram, Icon: Instagram, label: "Instagram" },
                  { href: church.social.youtube, Icon: Youtube, label: "YouTube" },
                ].map(({ href, Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={label}
                    data-testid={`footer-social-${label.toLowerCase()}`}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 hover:border-npbc-gold hover:text-npbc-gold transition"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-npbc py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/55">
          <div>© {new Date().getFullYear()} New Philadelphia Missionary Baptist Church. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <Link to="/visit" className="hover:text-white transition">Privacy Policy</Link>
            <span className="opacity-40">·</span>
            <span>Made with faith in Memphis, TN</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
